import React, { useState } from 'react';
import {
  Container, Card, CardContent, Typography, Button, Box,
  CircularProgress, Alert, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Tabs, Tab, MenuItem, Select, FormControl,
  InputLabel, IconButton
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import * as XLSX from 'xlsx';
import { API_URL } from '../../config';
import WhatsAppSendButton from '../common/WhatsAppSendButton';

const GREEN  = '#1b4332';
const ORANGE = '#f59e0b';

const HDR_SX = {
  fontWeight: 700, color: '#fff', fontSize: '0.75rem', textTransform: 'uppercase',
  background: `linear-gradient(135deg, ${ORANGE} 0%, #ffa726 100%)`,
  borderRight: '1px solid rgba(255,255,255,0.2)', py: 1.2, px: 0.8,
  whiteSpace: 'nowrap', textAlign: 'center',
};
const CELL_SX = { py: 1, px: 0.8, fontSize: '0.78rem', textAlign: 'center' };

function fmtNum(v, d = 0) {
  if (v === null || v === undefined || v === '') return '—';
  const n = parseFloat(v);
  return isNaN(n) ? '—' : n.toFixed(d);
}

function isGrandTotal(row) {
  return String(row.PRESSNO || row.COMPANY || '').trim().toUpperCase().startsWith('GRAND TOTAL');
}

// ─── Detail table ─────────────────────────────────────────────────────────────
function DetailTable({ rows }) {
  if (!rows || rows.length === 0) return <Alert severity="info" sx={{ m: 2 }}>No data</Alert>;
  return (
    <TableContainer sx={{ maxHeight: 580, overflowX: 'auto' }}>
      <Table stickyHeader size="small" sx={{ minWidth: 1600 }}>
        <TableHead>
          <TableRow>
            {[
              'SR. NO.', 'LOT NUMBER', 'PRESS NO.', 'LOAD TIME', 'UNLOAD TIME',
              'DURATION', 'OP TIME', 'CURING TIME', 'PCS', 'CBM',
              'SHIFT', 'THICKNESS', 'LENGTH', 'WIDTH', 'GLUE TYPE',
              'NO OF GLUE LINE', 'NO OF DRY LINE', 'NO OF FACE LINE', 'FACE CBM', 'GLUE LINE',
              'MARKET', 'COMPANY'
            ].map(h => (
              <TableCell key={h} sx={HDR_SX}>{h}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => (
            <TableRow key={i} sx={{
              backgroundColor: i % 2 === 0 ? '#fff' : '#f8faf9',
              '&:hover': { backgroundColor: '#e8f5e9' },
            }}>
              <TableCell sx={CELL_SX}>{i + 1}</TableCell>
              <TableCell sx={{ ...CELL_SX, fontWeight: 600 }}>{row.LOT_NUMBER || '—'}</TableCell>
              <TableCell sx={{ ...CELL_SX, fontWeight: 600, color: GREEN }}>{row.PRESSNO || row.HOTPRESS_PRESSNO || '—'}</TableCell>
              <TableCell sx={CELL_SX}>{row.LOADTIME || '—'}</TableCell>
              <TableCell sx={CELL_SX}>{row.UNLOADTIME || '—'}</TableCell>
              <TableCell sx={CELL_SX}>{fmtNum(row.DURATION, 2)}</TableCell>
              <TableCell sx={CELL_SX}>{fmtNum(row.OPTIME, 2)}</TableCell>
              <TableCell sx={CELL_SX}>{fmtNum(row.CURINGTIME, 2)}</TableCell>
              <TableCell sx={{ ...CELL_SX, fontWeight: 600, color: '#2e7d32' }}>{fmtNum(row.PCS, 0)}</TableCell>
              <TableCell sx={{ ...CELL_SX, fontWeight: 600, color: GREEN }}>{fmtNum(row.CBM, 3)}</TableCell>
              <TableCell sx={{ ...CELL_SX, fontWeight: 600, color: (row.HP_SHIFT || row.HOTPRESS_SHIFT) === 'NIGHT' ? '#1565c0' : '#2e7d32' }}>
                {row.HP_SHIFT || row.HOTPRESS_SHIFT || '—'}
              </TableCell>
              <TableCell sx={CELL_SX}>{fmtNum(row.THICKNESS, 2)}</TableCell>
              <TableCell sx={CELL_SX}>{fmtNum(row.LENGTH, 3)}</TableCell>
              <TableCell sx={CELL_SX}>{fmtNum(row.WIDTH, 3)}</TableCell>
              <TableCell sx={CELL_SX}>{row.GLUETYPE || '—'}</TableCell>
              <TableCell sx={{ ...CELL_SX, color: '#1565c0' }}>{fmtNum(row.NO_OF_GLUELINE, 0)}</TableCell>
              <TableCell sx={{ ...CELL_SX, color: '#1565c0' }}>{fmtNum(row.NO_OF_DRYLINE, 0)}</TableCell>
              <TableCell sx={{ ...CELL_SX, color: '#1565c0' }}>{fmtNum(row.NO_OF_FACE_LINE, 0)}</TableCell>
              <TableCell sx={{ ...CELL_SX, color: '#7c3aed' }}>{fmtNum(row.CBM_FACE, 3)}</TableCell>
              <TableCell sx={{ ...CELL_SX, color: '#7c3aed' }}>{fmtNum(row.GLUE_LINE, 3)}</TableCell>
              <TableCell sx={CELL_SX}>{row.MARKETNAME || '—'}</TableCell>
              <TableCell sx={{ ...CELL_SX, fontWeight: 600 }}>{row.COMPANY || '—'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ─── Summary table ────────────────────────────────────────────────────────────
function SummaryTable({ rows }) {
  if (!rows || rows.length === 0) return <Alert severity="info" sx={{ m: 2 }}>No data</Alert>;
  return (
    <TableContainer sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ minWidth: 400 }}>
        <TableHead>
          <TableRow>
            {['PRESS NO.', 'COMPANY', 'PCS', 'CBM'].map(h => (
              <TableCell key={h} sx={HDR_SX}>{h}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => {
            const grand = isGrandTotal(row);
            return (
              <TableRow key={i} sx={{
                backgroundColor: grand ? '#fef3c7' : (i % 2 === 0 ? '#fff' : '#f8faf9'),
                '&:hover': { filter: 'brightness(0.97)' },
              }}>
                <TableCell sx={{ ...CELL_SX, fontWeight: grand ? 700 : 600, color: grand ? '#92400e' : GREEN }}>
                  {row.PRESSNO || '—'}
                </TableCell>
                <TableCell sx={{ ...CELL_SX, fontWeight: grand ? 700 : 500, color: grand ? '#92400e' : '#374151' }}>
                  {row.COMPANY || '—'}
                </TableCell>
                <TableCell sx={{ ...CELL_SX, fontWeight: grand ? 700 : 600, color: grand ? '#92400e' : '#2e7d32' }}>
                  {fmtNum(row.TOTALPCS, 0)}
                </TableCell>
                <TableCell sx={{ ...CELL_SX, fontWeight: grand ? 700 : 600, color: grand ? '#92400e' : GREEN }}>
                  {fmtNum(row.TOTALCBM, 3)}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// Available machines — loaded dynamically from SP RS[2]
const TAB_LABELS = ['DETAIL', 'SUMMARY'];

const HotpressTransReportView = ({ onBackClick, user }) => {
  const [date, setDate]         = useState(null);
  const [shift, setShift]       = useState('BOTH');
  const [machine, setMachine]   = useState('ALL');
  const [machines, setMachines] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [data, setData]         = useState(null);
  const [tab, setTab]           = useState(0);

  // Load machine list on mount
  React.useEffect(() => {
    const loadMachines = async () => {
      try {
        const res = await fetch(`${API_URL}/order/hotpressmachines`);
        const apiData = await res.json();
        if (apiData.success && apiData.data?.length) setMachines(apiData.data);
      } catch (_) {}
    };
    loadMachines();
  }, []);

  const fetchData = async () => {
    if (!date) { setError('Please select a date'); return; }
    setLoading(true); setError(''); setData(null);
    try {
      const res = await fetch(`${API_URL}/order/hotpresstransreport`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date:    date.format('YYYY-MM-DD'),
          shift:   shift === 'BOTH' ? null : shift,
          machine: machine === 'ALL' ? null : machine,
        }),
      });
      if (!res.ok) throw new Error('API error: ' + res.statusText);
      const apiData = await res.json();
      if (!apiData.data || apiData.data.every(rs => rs.length === 0)) {
        setError('No data found for the selected filters.');
        return;
      }
      setData(apiData.data);
      setTab(0);
    } catch (e) {
      setError(e.message || 'Fetch failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!data) return;
    const wb = XLSX.utils.book_new();
    if (data[0]?.length) XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data[0]), 'Detail');
    if (data[1]?.length) XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data[1]), 'Summary');
    XLSX.writeFile(wb, `HPTrans_${shift}_${machine}_${date.format('YYYY-MM-DD')}.xlsx`);
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 2, pb: 4, px: { xs: 1, md: 2 } }}>
      {/* Header card */}
      <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 4px 12px rgba(27,67,50,0.1)', border: '1px solid #e8f5e9' }}>
        <CardContent sx={{ p: 3 }}>
          <Box display="flex" alignItems="center" mb={3}>
            {onBackClick && (
              <IconButton onClick={onBackClick} sx={{ mr: 2, color: GREEN }}>
                <ArrowBackIcon />
              </IconButton>
            )}
            <Box sx={{ background: `linear-gradient(135deg, ${GREEN} 0%, #2d6a4f 100%)`, borderRadius: 2, p: 1.5, mr: 2 }}>
              <LocalFireDepartmentIcon sx={{ fontSize: 28, color: '#fff' }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: GREEN }}>Hotpress Trans Report</Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>Press cycle detail by date, shift and machine</Typography>
            </Box>
          </Box>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2} alignItems="center">
              <DatePicker
                label="Date"
                value={date}
                onChange={setDate}
                slotProps={{ textField: { fullWidth: true, sx: { flex: 1 } } }}
              />
              <FormControl sx={{ minWidth: 180 }}>
                <InputLabel>Shift</InputLabel>
                <Select value={shift} label="Shift" onChange={e => setShift(e.target.value)}>
                  <MenuItem value="BOTH">BOTH (DAY + NIGHT)</MenuItem>
                  <MenuItem value="DAY">DAY</MenuItem>
                  <MenuItem value="NIGHT">NIGHT</MenuItem>
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 180 }}>
                <InputLabel>Machine</InputLabel>
                <Select value={machine} label="Machine" onChange={e => setMachine(e.target.value)}>
                  <MenuItem value="ALL">ALL MACHINES</MenuItem>
                  {machines.map(m => (
                    <MenuItem key={m.PRESSNO} value={m.PRESSNO}>
                      {m.PRESSNO} ({m.COMPANY})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                size="large"
                startIcon={<SearchIcon />}
                onClick={fetchData}
                disabled={loading}
                sx={{ minWidth: 140, backgroundColor: GREEN, '&:hover': { backgroundColor: '#0f2419' }, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
              >
                Search
              </Button>
            </Box>
          </LocalizationProvider>
        </CardContent>
      </Card>

      {loading && <Box textAlign="center" py={6}><CircularProgress sx={{ color: GREEN }} /></Box>}
      {error   && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {data && (
        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: '1px solid #e8f5e9' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, pt: 1 }}>
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              textColor="inherit"
              sx={{
                '& .MuiTab-root': { fontWeight: 600, fontSize: '0.78rem' },
                '& .Mui-selected': { color: GREEN },
                '& .MuiTabs-indicator': { backgroundColor: GREEN },
              }}
            >
              {TAB_LABELS.map((l, i) => <Tab key={i} label={l} />)}
            </Tabs>
            <Button size="small" startIcon={<DownloadIcon />} onClick={handleDownload}
              sx={{ color: GREEN, fontWeight: 600, textTransform: 'none', ml: 1, flexShrink: 0 }}>
              Excel
            </Button>
          </Box>

          {tab === 0 && <DetailTable  rows={data[0]} />}
          {tab === 1 && <SummaryTable rows={data[1]} />}
        </Card>
      )}

      {data && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: { xs: 'center', md: 'flex-end' } }}>
          <WhatsAppSendButton
            user={user}
            label="Send Hotpress Trans Report to WhatsApp"
            apiUrl={date ? `${API_URL}/api/hotpress-trans/send` : null}
            payload={{
              date:    date?.format('YYYY-MM-DD'),
              shift:   shift,
              machine: machine,
            }}
            disabled={!date}
          />
        </Box>
      )}
    </Container>
  );
};

export default HotpressTransReportView;
